/**
 * Экран отчёта по клавишам, сочетаниям и словам (ТЗ UX-07, UI-53 … UI-55).
 *
 * Сверху — сводка и тепловая карта слабых мест: человек приходит сюда с
 * вопросом «как у меня дела», а не «покажи таблицу». Разделы стоят ниже и
 * свёрнуты, кроме первого непустого: четыре таблицы подряд читаются стеной, и
 * до нужной приходилось листать.
 *
 * Четыре раздела показываются всегда, в том числе пустые: раздел, в котором
 * ничего не набрано, — это тоже ответ, а исчезнувший раздел читается как
 * «такого материала не бывает». Число строк стоит в заголовке даже у
 * свёрнутого — иначе непонятно, стоит ли раскрывать.
 *
 * Каждый раздел можно обыскать: поиск по содержанию, фильтр по состоянию и
 * страницы. Свёртывание их не сбрасывает: разделы остаются в разметке.
 *
 * Цвет ничего не кодирует в одиночку: доля ошибок и задержка стоят числами,
 * а освоенность дублируется словом (UX-06, требования доступности).
 */

import { useState } from 'react';
import type { Translate } from '@type2x/i18n';
import { Button, Card, EmptyState, HeatMap } from '@type2x/ui';
import type { ReportFilter, ReportRow, ReportSection, SkillReport } from '@type2x/feature-progress';
import { REPORT_FILTERS, viewSection } from '@type2x/feature-progress';

export interface ReportScreenProps {
  readonly t: Translate;
  readonly report: SkillReport;
  readonly onBack: () => void;
}

function percent(value: number): number {
  return Math.round(value * 100);
}

/** Сколько слабых мест показывает карта: больше десятка она не читается. */
const HEAT_CELLS = 10;

/** Сводка по всему отчёту: сколько единиц, сколько освоено, сколько ждёт. */
function summarize(report: SkillReport, now: string) {
  const rows = report.sections.flatMap((section) => section.rows);

  return {
    tracked: rows.length,
    mastered: rows.filter((row) => row.mastered).length,
    due: rows.filter((row) => row.nextReviewAt <= now).length,
  };
}

/**
 * Слабые места для карты.
 *
 * Берутся из всех разделов по возрастанию освоенности: карта отвечает на «что
 * хромает», а не «что где лежит». Освоенные в неё не попадают — они не слабые
 * места, и место в карте занимали бы зря.
 */
function weakest(report: SkillReport) {
  return report.sections
    .flatMap((section) => section.rows)
    .filter((row) => !row.mastered && row.attempts > 0)
    .sort((first, second) => first.masteryScore - second.masteryScore)
    .slice(0, HEAT_CELLS)
    .map((row) => ({ label: row.content, intensity: 1 - row.masteryScore }));
}

export function ReportScreen({ t, report, onBack }: ReportScreenProps): React.JSX.Element {
  const empty = report.sections.every((section) => section.rows.length === 0);
  const summary = summarize(report, report.builtAt);
  const weak = weakest(report);

  // Раскрыт первый непустой раздел: раскрывать пустой — значит показать
  // человеку пустое место вместо ответа.
  const firstFilled = report.sections.find((section) => section.rows.length > 0)?.id;

  return (
    <Card title={t('report.title')}>
      <p className="t2x-muted">{t('report.explain')}</p>

      {empty ? (
        <EmptyState title={t('report.emptyAllTitle')} body={t('report.emptyAll')} />
      ) : (
        <>
          <p className="t2x-report__summary">
            {t('report.summary', {
              tracked: summary.tracked,
              mastered: summary.mastered,
              due: summary.due,
            })}
          </p>

          {weak.length > 0 && (
            <HeatMap
              caption={t('report.weakest')}
              cells={weak}
              legendLabel={t('report.heatLegend')}
            />
          )}

          {report.sections.map((section) => (
            <SectionBlock
              key={section.id}
              t={t}
              section={section}
              now={report.builtAt}
              open={section.id === firstFilled}
            />
          ))}
        </>
      )}

      {report.untouched > 0 && (
        <p className="t2x-muted">{t('report.untouched', { count: report.untouched })}</p>
      )}

      <div className="t2x-actions">
        <Button onClick={onBack}>{t('report.back')}</Button>
      </div>
    </Card>
  );
}

/** Один раздел отчёта: поиск, фильтр, таблица и страницы. */
/**
 * Поиск, фильтр и страница раздела.
 *
 * Три состояния ходят вместе: смена запроса или фильтра сбрасывает страницу —
 * найденного может не быть там, где стоял человек, — а сброс возвращает все
 * три к началу.
 */
function useSectionState() {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ReportFilter>('all');
  const [page, setPage] = useState(1);

  return {
    query,
    filter,
    page,
    setQuery,
    setFilter,
    setPage,
    reset: () => {
      setQuery('');
      setFilter('all');
      setPage(1);
    },
  };
}

function SectionBlock({
  t,
  section,
  now,
  open,
}: {
  readonly t: Translate;
  readonly section: ReportSection;
  readonly now: string;
  /** Раскрыт ли раздел изначально: раскрыт первый непустой. */
  readonly open: boolean;
}): React.JSX.Element {
  const { query, filter, page, setQuery, setFilter, setPage, reset } = useSectionState();

  const view = viewSection(section, { query, filter, page, now });
  const title = t(`report.section.${section.id}`);

  return (
    <details className="t2x-report-section" open={open}>
      {/* Число строк стоит и у свёрнутого раздела: иначе непонятно, стоит ли
          его раскрывать (ТЗ UI-55). */}
      <summary>
        {/* Заголовок внутри раскрытия, а не вместо него: раздел остаётся
            заголовком для чтения с экрана, по которым и ходят по странице. */}
        <h3>
          {title} <span className="t2x-muted">{section.rows.length}</span>
        </h3>
      </summary>

      {section.rows.length === 0 ? (
        <EmptyState title={t('report.emptySectionTitle')} body={t('report.emptySection')} />
      ) : (
        <>
          <SectionControls
            t={t}
            title={title}
            query={query}
            filter={filter}
            onQuery={(value) => {
              setQuery(value);
              // Найденного может не быть на той странице, где стоял человек.
              setPage(1);
            }}
            onFilter={(value) => {
              setFilter(value);
              setPage(1);
            }}
          />

          {view.rows.length === 0 ? (
            <NothingFound t={t} onReset={reset} />
          ) : (
            <div className="t2x-report__scroll">
              <table className="t2x-report">
                <caption className="t2x-muted">
                  {t('report.shown', { shown: view.matched, total: view.total })}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">{t('report.column.unit')}</th>
                    <th scope="col">{t('report.column.attempts')}</th>
                    <th scope="col">{t('report.column.errors')}</th>
                    <th scope="col">{t('report.column.latency')}</th>
                    <th scope="col">{t('report.column.mastery')}</th>
                    <th scope="col">{t('report.column.review')}</th>
                  </tr>
                </thead>
                <tbody>
                  {view.rows.map((row) => (
                    <Row key={row.unitId} t={t} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {view.pages > 1 && (
            <Pages
              t={t}
              page={view.page}
              pages={view.pages}
              title={title}
              onPage={(value) => setPage(value)}
            />
          )}
        </>
      )}
    </details>
  );
}

/**
 * Поиск ничего не нашёл (ТЗ UI-68).
 *
 * Путь отсюда есть на самом деле: строки вернёт сброшенный поиск. Кнопка
 * ведёт туда, где данные уже лежат, — а не в никуда.
 */
function NothingFound({
  t,
  onReset,
}: {
  readonly t: Translate;
  readonly onReset: () => void;
}): React.JSX.Element {
  return (
    <EmptyState
      title={t('report.nothingFoundTitle')}
      body={t('report.nothingFound')}
      action={
        <Button variant="secondary" onClick={onReset}>
          {t('report.resetSearch')}
        </Button>
      }
    />
  );
}

/** Поиск и фильтр раздела. */
function SectionControls({
  t,
  title,
  query,
  filter,
  onQuery,
  onFilter,
}: {
  readonly t: Translate;
  readonly title: string;
  readonly query: string;
  readonly filter: ReportFilter;
  readonly onQuery: (value: string) => void;
  readonly onFilter: (value: ReportFilter) => void;
}): React.JSX.Element {
  return (
    <div className="t2x-report__controls">
      <label className="t2x-field">
        {/* Заголовок раздела входит в подпись: полей поиска на экране четыре,
            и «Поиск» у всех четырёх звучал бы одинаково. */}
        <span>{t('report.search', { section: title })}</span>
        <input type="search" value={query} onChange={(event) => onQuery(event.target.value)} />
      </label>

      <div role="group" aria-label={t('report.filterGroup', { section: title })}>
        {REPORT_FILTERS.map((value) => (
          <Button
            key={value}
            onClick={() => onFilter(value)}
            variant={value === filter ? 'primary' : 'secondary'}
          >
            {t(`report.filter.${value}`)}
          </Button>
        ))}
      </div>
    </div>
  );
}

/** Переход по страницам раздела. */
function Pages({
  t,
  page,
  pages,
  title,
  onPage,
}: {
  readonly t: Translate;
  readonly page: number;
  readonly pages: number;
  readonly title: string;
  readonly onPage: (value: number) => void;
}): React.JSX.Element {
  return (
    <div
      className="t2x-report__pages"
      role="group"
      aria-label={t('report.pageGroup', { section: title })}
    >
      <Button onClick={() => onPage(page - 1)} disabled={page <= 1}>
        {t('report.prev')}
      </Button>
      <span>{t('report.page', { page, pages })}</span>
      <Button onClick={() => onPage(page + 1)} disabled={page >= pages}>
        {t('report.next')}
      </Button>
    </div>
  );
}

/**
 * Строка отчёта: одна учебная единица.
 *
 * У каждой ячейки стоит подпись столбца атрибутом: на телефоне таблица
 * превращается в карточки, и подпись становится видимой (ТЗ UI-54). Берётся
 * она из словаря, как и заголовок столбца, — второй список подписей разошёлся
 * бы с первым.
 */
function Row({ t, row }: { readonly t: Translate; readonly row: ReportRow }): React.JSX.Element {
  return (
    <tr className={row.mastered ? 't2x-report__row--mastered' : undefined}>
      <th scope="row" className="t2x-report__unit">
        {row.content}
      </th>
      <td data-label={t('report.column.attempts')}>{row.attempts}</td>
      <td data-label={t('report.column.errors')}>{percent(row.errorRate)}%</td>
      <td data-label={t('report.column.latency')}>
        {t('report.ms', { value: Math.round(row.medianLatencyMs) })}
      </td>
      <td data-label={t('report.column.mastery')}>
        {percent(row.masteryScore)}%{' '}
        <span className="t2x-muted">{t(row.mastered ? 'report.mastered' : 'report.learning')}</span>
      </td>
      <td data-label={t('report.column.review')}>{row.nextReviewAt.slice(0, 10)}</td>
    </tr>
  );
}
